// src/lib/queue.ts
//
// Which section of the approval queue a bill belongs in — the three-way split
// `ApprovalQueuePage` renders, pulled out of the page so it can be asserted.
//
// ══ WHY THE SPLIT IS THREE-WAY ══
//
// Two was a lie on real data. Every uncomputed SUBMITTED bill offers nothing (an
// approval needs a split; the reject route refuses SUBMITTED) — but it is NOT
// "awaiting someone else". It is waiting on a step the very officer looking at
// it can take, one click away on the bill's own screen, and filing it under
// somebody else's name tells a level 2 approver to go and wait for themselves.
//
// ══ THE PROMISE THE MIDDLE SECTION MAKES, AND WHY IT IS NOW LOAD-BEARING ══
//
// "Waiting on a pro-ration split" tells the officer to open the bill and record
// one. That is an instruction, so it is only honest for a bill this officer can
// actually record a split on — and until now membership was decided WITHOUT
// asking. It happened to be correct, by a route worth writing down, because it
// was a coincidence of two tables in `packages/auth/src/scope.ts` rather than
// anything this page checked:
//
//   · An `AWAITING_L1`/`AWAITING_L2` bill never reaches this section at all,
//     because `REJECT` carries the same authority as the approval it sits beside
//     (level 1, ceiling binding) — so an officer blocked only by the missing
//     split can always REJECT, which makes the row actionable and files it under
//     "Awaiting your decision".
//   · That leaves `SUBMITTED` bills, whose split is an ORDINARY compute: level
//     0, ceiling exempt. Anyone holding a covering grant can record it.
//
// Both halves are properties of tables this file does not own. If a later policy
// raised the repair's bar above the approval's — or gave `REJECT` an authority
// of its own — this section would start telling officers to perform work the
// gateway would refuse them, silently, and the page would look exactly the same.
// So membership now ASKS `computeOffer`, and `queue.test.ts` pins the
// coincidence separately so that a divergence shows up as a failing test rather
// than as a wrong instruction on screen.
import { billAuthority, computeOffer, type BillAuthority, type ComputeOffer } from './authority'
import type { Bill, Scope } from './types'

/** One queued bill, with both questions already asked about it. */
export interface QueueAssessment {
  readonly bill: Bill
  readonly authority: BillAuthority
  /** Which compute this bill would be for this officer, or `null` for none. */
  readonly offer: ComputeOffer | null
}

export interface QueueSections {
  /** This officer holds the grants these need. */
  readonly mine: readonly QueueAssessment[]
  /** Blocked only by a missing split — and this officer can record it. */
  readonly blockedOnCompute: readonly QueueAssessment[]
  /** Visible, but outside what this officer's grants let them do. */
  readonly theirs: readonly QueueAssessment[]
}

/**
 * True when the ONLY thing standing between this officer and a decision is the
 * missing split — the precondition `billAuthority` reports last, after every
 * authority axis has passed.
 */
function blockedOnlyBySplit(assessment: QueueAssessment): boolean {
  const { authority } = assessment
  return !authority.hasAnyAction && authority.approve?.allowed === false && authority.approve.why.kind === 'not-computed'
}

/**
 * True when this officer can actually record that split — the question the
 * section's instruction depends on and never used to ask.
 */
export function mayRecordSplit(assessment: QueueAssessment): boolean {
  return assessment.offer?.availability.allowed === true
}

export function assessQueue(scope: Scope, bills: readonly Bill[]): readonly QueueAssessment[] {
  // One authority verdict and one compute offer per bill, computed once and
  // carried into every section — a second call site would be a second place to
  // get the split wrong.
  return bills.map((bill) => ({ bill, authority: billAuthority(scope, bill), offer: computeOffer(scope, bill) }))
}

export function queueSections(assessed: readonly QueueAssessment[]): QueueSections {
  const mine = assessed.filter((row) => row.authority.hasAnyAction)
  // ⚠ BOTH conditions. "Blocked on the split" earns this section only together
  // with "and you can record it"; a row that fails the second half is genuinely
  // waiting on somebody else and is filed as such, with the reason said out
  // loud — see `reasonsFor` in `ApprovalQueuePage`.
  const blockedOnCompute = assessed.filter((row) => blockedOnlyBySplit(row) && mayRecordSplit(row))
  const theirs = assessed.filter((row) => !row.authority.hasAnyAction && !(blockedOnlyBySplit(row) && mayRecordSplit(row)))
  return { mine, blockedOnCompute, theirs }
}

/**
 * True when this row should also be told WHY it cannot have its split recorded —
 * blocked on the split, and unable to record it.
 *
 * Separate from {@link mayRecordSplit} because a row with no offer at all
 * (`null`) has no sentence to render: there is no compute to explain.
 */
export function needsSplitRefusal(assessment: QueueAssessment): boolean {
  return blockedOnlyBySplit(assessment) && assessment.offer !== null && !assessment.offer.availability.allowed
}
